import { Badge, Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { X } from 'lucide-react';
import type { ActiveColumnFilter, ColumnFilterConfig, UseColumnFiltersResult } from '@/hooks/use-column-filters';

// column-picker + chips UI extracted from payout-accounts-panel.tsx and
// login-history-table.tsx (near-verbatim twins). Chips render in the primary
// colour (Badge default variant; the secondary purple is deliberately unused),
// with Clear-all as Button variant="ghost" — the canonical treatment for this
// variant. This
// is unrelated to the dialog-per-column TableFilters<TRow> variant (src/features/
// institutions/table-filters.tsx), whose count-badge/Clear-All styling differs and
// is out of scope here.

const titleOf = <TRow,>(configs: ColumnFilterConfig<TRow>[], field: string): string => configs.find((config) => config.field === field)?.title ?? field;

export interface FilterChipsProps<TRow> {
  filters: ActiveColumnFilter[];
  configs: ColumnFilterConfig<TRow>[];
  removeFilter: (target: ActiveColumnFilter) => void;
  clearAll: () => void;
}

/** The applied-filter chip row alone, for surfaces that add filters elsewhere (e.g. column-header popovers). */
export const FilterChips = <TRow,>({ filters, configs, removeFilter, clearAll }: FilterChipsProps<TRow>) => {
  if (filters.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {filters.map((filter: ActiveColumnFilter) => (
        <Badge className="gap-1 pr-1" key={`${filter.field}:${filter.value}`}>
          <span>
            {titleOf(configs, filter.field)}: {filter.value}
          </span>
          <button
            aria-label={`Remove ${titleOf(configs, filter.field)} filter ${filter.value}`}
            className="rounded-full p-0.5 hover:bg-muted-foreground/20"
            onClick={() => removeFilter(filter)}
            type="button"
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <Button onClick={clearAll} size="sm" type="button" variant="ghost">
        Clear All
      </Button>
    </div>
  );
};
FilterChips.displayName = 'FilterChips';

export interface ColumnFilterBarProps<TRow> {
  /** Return value of useColumnFilters(data, configs, ...) for the same configs below. */
  state: UseColumnFiltersResult<TRow>;
  /** The filterable column configs backing the "Filter column" picker — same array passed to useColumnFilters. */
  configs: ColumnFilterConfig<TRow>[];
  /** Unique DOM id prefix so multiple bars on one page don't collide (e.g. 'payout', 'login-history'). */
  idPrefix: string;
}

export const ColumnFilterBar = <TRow,>({ state, configs, idPrefix }: ColumnFilterBarProps<TRow>) => {
  const { filters, activeFilterConfig, choiceOptions, filterText, setFilterField, setFilterText, addFilter, removeFilter, submitTextFilter, handleFilterKeyDown, clearAll } = state;

  if (!activeFilterConfig && filters.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {activeFilterConfig ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-filter-column`}>Filter column</Label>
            <Select onValueChange={setFilterField} value={activeFilterConfig.field}>
              <SelectTrigger className="w-48" id={`${idPrefix}-filter-column`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {configs.map((config) => (
                  <SelectItem key={config.field} value={config.field}>
                    {config.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {activeFilterConfig.filter === 'choice' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-filter-value`}>Value</Label>
              {/* value stays '' so each pick lands as a chip and the trigger resets */}
              <Select onValueChange={(value) => addFilter(activeFilterConfig.field, value)} value="">
                <SelectTrigger className="w-56" id={`${idPrefix}-filter-value`}>
                  <SelectValue placeholder="Add a value…" />
                </SelectTrigger>
                <SelectContent>
                  {choiceOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${idPrefix}-filter-value`}>Value</Label>
                <Input
                  className="w-56"
                  id={`${idPrefix}-filter-value`}
                  onChange={(event) => setFilterText(event.target.value)}
                  onKeyDown={handleFilterKeyDown}
                  placeholder={`Filter ${activeFilterConfig.title}…`}
                  value={filterText}
                />
              </div>
              <Button disabled={!filterText.trim()} onClick={submitTextFilter} type="button" variant="outline">
                Add Filter
              </Button>
            </>
          )}
        </div>
      ) : null}

      <FilterChips clearAll={clearAll} configs={configs} filters={filters} removeFilter={removeFilter} />
    </div>
  );
};
ColumnFilterBar.displayName = 'ColumnFilterBar';
